import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../supabaseClient'
import { BleTelemetryController, isWebBluetoothAvailable } from './bleManager'
import type { RecoveryState } from './connectionState'
import { InterventionResolver, type InterventionEvent } from './interventionResolver'
import { SessionAggregator, type SessionMarkers } from './sessionAggregator'
import { TelemetryBuffer, type TelemetryWindow } from './telemetryBuffer'
import type {
  DiscoveredPeripheral,
  PeripheralConnectionState,
  TelemetryFrame,
  TelemetryMetric,
} from './types'

type LiveMetrics = Partial<Record<TelemetryMetric, number>>

export type UseLiveTelemetry = {
  available: boolean
  state: PeripheralConnectionState
  recoveryState: RecoveryState
  peripheral: DiscoveredPeripheral | null
  metrics: LiveMetrics
  intervention: InterventionEvent | null
  error: string | null
  discoverAndStream: () => Promise<void>
  dismissIntervention: () => void
  resumeFromFallback: () => void
  endSession: () => Promise<{ markers: SessionMarkers; strainScore: number | null }>
  disconnect: () => Promise<void>
}

/** A live, already-streaming controller handed off from the pairing step. */
export type PreconnectedSession = {
  controller: BleTelemetryController
  peripheral: DiscoveredPeripheral
}

/**
 * Binds the BLE controller, session aggregator and intervention resolver into a
 * single React surface for the live workout HUD.
 *
 * When `preconnected` is supplied (handed off from the pairing scan) the hook
 * adopts that live controller instead of discovering again — avoiding a second
 * OS Bluetooth prompt.
 */
export function useLiveTelemetry(
  targetHr: number,
  preconnected?: PreconnectedSession | null,
): UseLiveTelemetry {
  const controllerRef = useRef<BleTelemetryController | null>(null)
  // Lazily-initialized singletons held in state so the instance is stable for
  // the lifetime of the hook without touching refs during render.
  const [resolver] = useState(() => new InterventionResolver(targetHr))
  const [aggregator] = useState(() => new SessionAggregator())
  const bufferRef = useRef<TelemetryBuffer | null>(null)
  const collectedWindowsRef = useRef<TelemetryWindow[]>([])
  const adoptedRef = useRef(false)

  const [state, setState] = useState<PeripheralConnectionState>(preconnected ? 'streaming' : 'idle')
  const [recoveryState, setRecoveryState] = useState<RecoveryState>('CONNECTED')
  const [peripheral, setPeripheral] = useState<DiscoveredPeripheral | null>(preconnected?.peripheral ?? null)
  const [metrics, setMetrics] = useState<LiveMetrics>({})
  const [intervention, setIntervention] = useState<InterventionEvent | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    resolver.setTargetHr(targetHr)
  }, [resolver, targetHr])

  // Attach buffer/aggregator/resolver + listeners to a controller. Shared by
  // both the discover path and the pre-connected adoption path.
  const wireController = useCallback((controller: BleTelemetryController) => {
    controllerRef.current = controller
    aggregator.reset()

    // Buffer folds 1Hz frames into 10s windows and flushes batches off the hot
    // path; collected windows are drained into the final session payload.
    collectedWindowsRef.current = []
    const buffer = new TelemetryBuffer((windows) => {
      collectedWindowsRef.current.push(...windows)
    })
    bufferRef.current = buffer
    buffer.start()

    controller.onStateChange((s, detail) => {
      setState(s)
      if (s === 'error' && detail) setError(detail)
    })
    controller.onRecoveryStateChange((rState, gap) => {
      setRecoveryState(rState)
      // Linear backfill for short, recovered drops so curves stay continuous.
      if (rState === 'CONNECTED' && gap?.backfillable) {
        bufferRef.current?.backfillGap(gap.droppedAt, gap.recoveredAt)
      }
    })
    controller.onFrame((frame: TelemetryFrame) => {
      aggregator.ingest(frame)
      resolver.ingest(frame)
      bufferRef.current?.ingest(frame)
      setMetrics((prev) => ({ ...prev, ...frame.metrics }))
    })

    resolver.onIntervention((event) => setIntervention(event))
    resolver.start()
  }, [aggregator, resolver])

  const discoverAndStream = useCallback(async () => {
    setError(null)
    if (!isWebBluetoothAvailable()) {
      setError('Web Bluetooth is unavailable. Use Chrome or Edge over HTTPS.')
      setState('error')
      return
    }

    const controller = new BleTelemetryController()
    wireController(controller)

    try {
      await controller.discover()
      const connected = await controller.connectAndStream()
      setPeripheral(connected)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed')
      setState('error')
    }
  }, [wireController])

  // Adopt a controller already discovered + streaming by the pairing step.
  useEffect(() => {
    if (!preconnected || adoptedRef.current) return
    adoptedRef.current = true
    wireController(preconnected.controller)
    setPeripheral(preconnected.peripheral)
    setState(preconnected.controller.getState())
  }, [preconnected, wireController])

  const dismissIntervention = useCallback(() => setIntervention(null), [])

  const resumeFromFallback = useCallback(() => {
    controllerRef.current?.resumeFromFallback()
  }, [])

  const endSession = useCallback(async () => {
    resolver.stop()
    // Seal the trailing window and drain all batched windows before teardown.
    await bufferRef.current?.finalizeAndFlush().catch(() => undefined)
    await controllerRef.current?.disconnect().catch(() => undefined)
    const markers = aggregator.finalize()
    const windows = collectedWindowsRef.current

    let strainScore: number | null = null
    try {
      const maxHrEstimate = markers.maxHeartRate > 0 ? markers.maxHeartRate + 5 : undefined
      const { data } = await supabase.functions.invoke('finalize-live-session', {
        body: {
          markers,
          windows_summary: { count: windows.length },
          max_hr_estimate: maxHrEstimate,
          device_brand_slug: peripheral?.name ?? null,
        },
      })
      const payload = data as { strain_score?: number } | null
      strainScore = payload?.strain_score ?? null
    } catch (err) {
      console.warn('finalize-live-session invoke failed:', err)
    }

    return { markers, strainScore }
  }, [aggregator, resolver, peripheral])

  const disconnect = useCallback(async () => {
    resolver.stop()
    await bufferRef.current?.finalizeAndFlush().catch(() => undefined)
    await controllerRef.current?.disconnect().catch(() => undefined)
    setState('disconnected')
  }, [resolver])

  useEffect(() => {
    return () => {
      resolver.stop()
      void bufferRef.current?.finalizeAndFlush().catch(() => undefined)
      void controllerRef.current?.disconnect().catch(() => undefined)
    }
  }, [resolver])

  return {
    available: isWebBluetoothAvailable(),
    state,
    recoveryState,
    peripheral,
    metrics,
    intervention,
    error,
    discoverAndStream,
    dismissIntervention,
    resumeFromFallback,
    endSession,
    disconnect,
  }
}
